import { useCallback, useContext } from 'react';
import { SessionContext } from '../context/SessionContext';
import * as GA from '../utils/analytics';

export interface EventData {
    [key: string]: any;
}

export interface EventPayload {
    session_id: string;
    event_type: EventType;
    user_agent?: string;
    ip_address?: string;
}

export type EventType =
    | { type: 'SessionStarted' }
    | { type: 'PageViewed'; path: string; referrer?: string; viewport_width?: number; viewport_height?: number }
    | { type: 'ElementClicked'; element_id: string; element_text?: string; element_type?: string; page_path: string }
    | { type: 'FormFieldChanged'; field_name: string; field_type: string; form_id: string }
    | { type: 'AssessmentAnswered'; question_id: string; question: string; answer: string; answer_index?: number }
    | { type: 'AssessmentCompleted'; score: number; responses: any; completion_time_seconds: number }
    | { type: 'ContactFormSubmitted'; name: string; email: string; company?: string; message: string; source?: string; phone?: string; }
    | { type: 'SessionEnded'; duration_seconds: number; page_views: number; interactions: number };

export const useEventTracking = () => {
    const { sessionId, isLoading } = useContext(SessionContext);

    const sendEvent = useCallback(async (eventType: EventType): Promise<boolean> => {
        // Wait for session to load if still loading
        if (isLoading) {
            return false;
        }

        if (!sessionId) {
            console.warn('No session ID available for event tracking');
            return false;
        }

        try {
            const payload: EventPayload = {
                session_id: sessionId,
                event_type: eventType,
                user_agent: navigator.userAgent,
            };

            const response = await fetch('/api/events', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return true;
        } catch (error) {
            return false;
        }
    }, [sessionId, isLoading]);

    const trackPageView = useCallback((path: string, referrer?: string) => {
        // Only track if session is ready
        if (isLoading || !sessionId) {
            return Promise.resolve(false);
        }

        // Simple deduplication - don't track the same page twice in rapid succession
        const lastPageView = sessionStorage.getItem('last_page_view');
        const currentPageView = `${path}_${Date.now()}`;
        const timeSinceLastView = lastPageView ? Date.now() - parseInt(lastPageView.split('_')[1]) : Infinity;

        if (lastPageView && lastPageView.startsWith(path) && timeSinceLastView < 5000) {
            return Promise.resolve(false);
        }

        sessionStorage.setItem('last_page_view', currentPageView);

        const viewport_width = window.innerWidth;
        const viewport_height = window.innerHeight;

        // Track to your backend
        const backendTracking = sendEvent({
            type: 'PageViewed',
            path,
            referrer,
            viewport_width,
            viewport_height,
        });

        // Also track to Google Analytics
        GA.trackPageView(path, document.title);

        return backendTracking;
    }, [sendEvent, sessionId, isLoading]);

    const trackClick = useCallback((elementId: string, elementText?: string, elementType?: string) => {
        if (isLoading || !sessionId) {
            return Promise.resolve(false);
        }

        const page_path = window.location.pathname;

        // Track to your backend
        const backendTracking = sendEvent({
            type: 'ElementClicked',
            element_id: elementId,
            element_text: elementText,
            element_type: elementType,
            page_path,
        });

        // Also track to Google Analytics
        GA.trackClick(elementId, elementType as 'link' | 'button');

        return backendTracking;
    }, [sendEvent, sessionId, isLoading]);

    const trackFormFieldChange = useCallback(
        debounce((fieldName: string, fieldType: string, formId: string) => {
            if (isLoading || !sessionId) return Promise.resolve(false);

            // Track to your backend
            const backendTracking = sendEvent({
                type: 'FormFieldChanged',
                field_name: fieldName,
                field_type: fieldType,
                form_id: formId,
            });

            // Track to Google Analytics (form interaction)
            GA.trackEvent('form_field_change', {
                field_name: fieldName,
                field_type: fieldType,
                form_id: formId,
            });

            return backendTracking;
        }, 2000), // Wait 2 second after user stops typing
        [sendEvent, sessionId, isLoading]
    );

    const trackAssessmentAnswer = useCallback((
        questionId: string,
        question: string,
        answer: string,
        answerIndex?: number
    ) => {
        if (isLoading || !sessionId) return Promise.resolve(false);

        // Track to your backend
        const backendTracking = sendEvent({
            type: 'AssessmentAnswered',
            question_id: questionId,
            question,
            answer,
            answer_index: answerIndex,
        });

        // Track to Google Analytics
        GA.trackAssessment('answer', {
            question_id: questionId,
            answer_index: answerIndex,
        });

        return backendTracking;
    }, [sendEvent, sessionId, isLoading]);

    const trackAssessmentComplete = useCallback((
        score: number,
        responses: any,
        completionTimeSeconds: number
    ) => {
        if (isLoading || !sessionId) return Promise.resolve(false);

        // Track to your backend
        const backendTracking = sendEvent({
            type: 'AssessmentCompleted',
            score,
            responses,
            completion_time_seconds: completionTimeSeconds,
        });

        // Track to Google Analytics
        GA.trackAssessment('complete', {
            score: score,
            completion_time: completionTimeSeconds,
        });

        return backendTracking;
    }, [sendEvent, sessionId, isLoading]);

    const trackContactFormSubmit = useCallback((
        name: string,
        email: string,
        message: string,
        company?: string,
        source?: string,
        phone?: string,
    ) => {
        if (isLoading || !sessionId) return Promise.resolve(false);

        // Track to your backend
        const backendTracking = sendEvent({
            type: 'ContactFormSubmitted',
            name,
            email,
            company,
            message,
            source,
            phone,
        });

        // Track to Google Analytics
        GA.trackFormSubmission('contact_form', true);

        return backendTracking;
    }, [sendEvent, sessionId, isLoading]);

    const trackSessionEnd = useCallback((
        durationSeconds: number,
        pageViews: number,
        interactions: number
    ) => {
        if (isLoading || !sessionId) return Promise.resolve(false);

        // Track to your backend
        const backendTracking = sendEvent({
            type: 'SessionEnded',
            duration_seconds: durationSeconds,
            page_views: pageViews,
            interactions,
        });

        // Track to Google Analytics
        GA.trackEvent('session_end', {
            session_duration: durationSeconds,
            page_views: pageViews,
            interactions: interactions,
        });

        return backendTracking;
    }, [sendEvent, sessionId, isLoading]);

    return {
        trackPageView,
        trackClick,
        trackFormFieldChange,
        trackAssessmentAnswer,
        trackAssessmentComplete,
        trackContactFormSubmit,
        trackSessionEnd,
        sendEvent,
        // Export session state for components that need to know
        isSessionReady: !isLoading && !!sessionId,
    };
};

// Add at the top of useEventTracking.ts
function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    // @ts-ignore
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}